import { DamageElement, EffectId } from "./constants.js";
/**
 * Condições (PRD §6): veneno, fogo, sangramento e paralisia.
 *
 * O catálogo mora no protocolo, e não só no servidor, pelo mesmo motivo que o de magias:
 * o cliente precisa dele para **explicar ao jogador o que está acontecendo com ele**. Um
 * personagem que perde vida parado, sem nada na tela dizendo por quê, é indistinguível de
 * um bug de rede — e a primeira reação de quem joga é reclamar do servidor.
 *
 * O que fica do lado do servidor é a outra metade: o estado por criatura e o desconto por
 * tick, que são de quem é dono do mundo e não cruzam a fronteira da thread.
 */
export const ConditionId = {
    /** Dano residual lento e longo. Não impede nada, só encurta o prazo. */
    Poison: 0,
    /** Queimadura: mais dano por pulso que o veneno, e menos tempo. */
    Fire: 1,
    /** Sangramento: o que uma garra deixa. Curto e agressivo. */
    Bleed: 2,
    /** Paralisia: não fere, atrasa o passo. Ver `stepFactor`. */
    Paralysis: 3,
    /** Haste: não fere, apressa o passo. O espelho benigno da paralisia. */
    Haste: 4,
};
export const CONDITION_COUNT = 5;
/**
 * Posição da condição na máscara de bits que trafega no `Stats`.
 *
 * A máscara existe para que o caso comum — ninguém com condição alguma — custe **uma
 * comparação por criatura por tick**, em vez de quatro leituras de objeto. Com mil
 * jogadores por região (PRD §2) e vinte ticks por segundo, essa diferença é o tipo de
 * coisa que o PRD §3.1 chama de caminho quente.
 */
export function conditionBit(id) {
    return 1 << id;
}
/**
 * Indexado por `ConditionId` — vetor e não objeto, porque o laço do tick percorre os
 * quatro por índice e um `Object.values()` por criatura por tick seria alocação pura.
 */
export const CONDITIONS = [
    {
        id: ConditionId.Poison,
        name: 'Envenenado',
        pulseMs: 2000,
        stepFactor: 1,
        effect: EffectId.Poison,
        element: DamageElement.Earth,
    },
    {
        id: ConditionId.Fire,
        name: 'Em chamas',
        pulseMs: 1000,
        stepFactor: 1,
        effect: EffectId.Fire,
        element: DamageElement.Fire,
    },
    {
        id: ConditionId.Bleed,
        name: 'Sangrando',
        pulseMs: 1500,
        stepFactor: 1,
        effect: EffectId.Bleed,
        // Sangramento é o corte que continua sangrando: físico, e não um elemento próprio.
        element: DamageElement.Physical,
    },
    {
        /**
         * Paralisia não fere: `pulseMs` zero e `damage` sempre zero em quem a aplica.
         *
         * Ela atrasa o passo em 80% em vez de impedi-lo, e a diferença importa. Imobilizar por
         * completo já existe — é a magia "Raízes" (§9.3), com duração curta e recarga longa
         * justamente porque tirar o controle do personagem é a coisa mais frustrante que um
         * jogo pode fazer. Andar devagar mantém a decisão nas mãos de quem joga: dá para fugir,
         * só que apanhando no caminho.
         */
        id: ConditionId.Paralysis,
        name: 'Paralisado',
        pulseMs: 0,
        stepFactor: 1.8,
        effect: EffectId.Ice,
        element: DamageElement.Ice,
    },
    {
        /**
         * Haste não fere: como a paralisia, ela é só `stepFactor` — na direção oposta.
         *
         * `1 / 1.3`, e não um decimal arredondado: a promessa da magia "Agilidade" é **+30% de
         * velocidade**, e velocidade é o inverso da duração do passo. Escrever a fração deixa a
         * promessa conferível nesta linha, em vez de escondida num `0.77` que ninguém sabe de
         * onde veio.
         */
        id: ConditionId.Haste,
        name: 'Acelerado',
        pulseMs: 0,
        stepFactor: 1 / 1.3,
        effect: EffectId.Charge,
        element: DamageElement.Energy,
    },
];
export function conditionInfo(id) {
    return CONDITIONS[id];
}
/** Nomes das condições ativas numa máscara, para a interface listar. */
export function conditionNames(mask) {
    const names = [];
    for (let id = 0; id < CONDITION_COUNT; id++) {
        if ((mask & conditionBit(id)) !== 0)
            names.push(CONDITIONS[id].name);
    }
    return names;
}
//# sourceMappingURL=conditions.js.map